import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Server, TrendingUp, Users, ArrowRight, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import Layout from '@/components/Layout';
import LogoMarquee from '@/components/LogoMarquee';
import AEOBlock from '@/components/AEOBlock';
import GlassCard from '@/components/GlassCard';
import FloatingOrbs from '@/components/FloatingOrbs';
import ScrollReveal from '@/components/ScrollReveal';
import heroImageJpg from '@/assets/chuck-boyce-hero-city.jpg';
import heroImageMobile from '@/assets/chuck-boyce-hero-city-mobile.jpg';

const Index = () => {
  return (
    <Layout>
      <Helmet>
        <title>Chuck Boyce | Technologist & Strategic Leader</title>
        <meta
          name="description"
          content="Chuck Boyce is a veteran Technologist with 30 years of experience in technical operations, strategic digital marketing, and systems thinking applied to community leadership."
        />
        <link rel="canonical" href="https://chuckboyce.com/" />

        {/* LCP hero image preload lives in index.html's static <head>, not here —
            a Helmet-injected preload doesn't exist until React has already mounted,
            which is too late for Chrome's preload scanner to act on it. */}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chuckboyce.com/" />
        <meta property="og:title" content="Chuck Boyce | Technologist & Strategic Leader" />
        <meta property="og:description" content="Chuck Boyce is a veteran Technologist with 30 years of experience in technical operations, strategic digital marketing, and systems thinking applied to community leadership." />
        <meta property="og:image" content="https://chuckboyce.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Chuck Boyce | Technologist & Strategic Leader" />
        <meta name="twitter:description" content="Chuck Boyce is a veteran Technologist with 30 years of experience in technical operations, strategic digital marketing, and systems thinking applied to community leadership." />
        <meta name="twitter:image" content="https://chuckboyce.com/og-image.png" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Floating Orbs Background */}
        <FloatingOrbs />
        
        {/* Hero Background Image - Optimized for LCP with WebP */}
        <div className="absolute inset-0">
          <img
            src={heroImageMobile}
            srcSet={`${heroImageMobile} 960w, ${heroImageJpg} 1920w`}
            sizes="(max-width: 768px) 100vw, 1920px"
            alt="Chuck Boyce - Veteran Technologist with 30 years of experience"
            className="w-full h-full object-cover object-top"
            width={1920}
            height={1080}
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-2xl">
            <motion.p 
              className="text-primary font-medium mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Veteran Technologist
            </motion.p>
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Chuck Boyce: 30 Years of Technical Innovation & Strategic Leadership
            </motion.h1>
            <motion.p 
              className="text-lg text-muted-foreground mb-8 max-w-lg"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              From engineering management studies at Stevens to strategic exits, 
              applying systems thinking to transform technology, business, and community.
            </motion.p>
            <motion.div 
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Link to="/about" className="btn-primary inline-flex items-center gap-2 group">
                <span>Explore My Journey</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/publications"
                className="px-6 py-3 rounded-lg font-medium border border-border bg-background/50 backdrop-blur-sm text-foreground hover:bg-background/80 hover:border-primary/50 transition-all inline-flex items-center gap-2"
              >
                <BookOpen size={18} />
                <span>Read My Books</span>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
        >
          <motion.div
            className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex justify-center pt-2"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="w-1 h-2 rounded-full bg-primary" />
          </motion.div>
        </motion.div>
      </section>

      {/* Logo Marquee */}
      <LogoMarquee />

      {/* AEO Block Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <ScrollReveal className="max-w-3xl mx-auto">
            <AEOBlock
              question="How do 30 years of engineering management inform modern digital strategy?"
              answer="Chuck Boyce's three-decade journey from engineering management studies at Stevens Institute of Technology through founding multiple technology companies and achieving a strategic exit to LegalZoom provides a unique lens for modern digital strategy. His approach combines rigorous systems thinking with practical business acumen—understanding that sustainable digital transformation requires both technical excellence and strategic vision. This methodology, now codified as the Beacon Method™, bridges the gap between complex technical operations and measurable business outcomes."
            />
          </ScrollReveal>
        </div>
      </section>

      {/* Expertise Cards */}
      <section className="py-16 md:py-24 bg-muted/20 relative overflow-hidden">
        {/* Background gradient accent */}
        <div 
          className="absolute top-0 right-0 w-1/2 h-full opacity-30 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 100% 0%, hsl(var(--primary) / 0.2) 0%, transparent 50%)',
          }}
        />
        
        <div className="container mx-auto px-6 relative z-10">
          <ScrollReveal className="text-center mb-12">
            <h2 className="section-heading text-foreground mb-4">Areas of Expertise</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Three decades of experience distilled into core competencies that drive organizational transformation.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-6">
            <GlassCard
              title="Technical Operations"
              description="Expertise in PLM systems, infrastructure scaling, and building technical foundations that support enterprise growth."
              icon={Server}
              link="/technical-operations"
              index={0}
            />
            <GlassCard
              title="Strategic Digital Marketing"
              description="Data-driven growth strategies and MarTech architecture that transform digital presence into measurable results."
              icon={TrendingUp}
              link="/digital-marketing"
              index={1}
            />
            <GlassCard
              title="Public Service"
              description="Applying systems thinking to community leadership, bringing engineering discipline to civic challenges."
              icon={Users}
              link="/public-service"
              index={2}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <FloatingOrbs />
        <div className="container mx-auto px-6 relative z-10">
          <ScrollReveal>
            <div 
              className="relative p-8 md:p-12 text-center max-w-3xl mx-auto rounded-2xl overflow-hidden cta-card"
              style={{
                background: 'linear-gradient(135deg, hsl(var(--card) / 0.9) 0%, hsl(var(--muted) / 0.5) 100%)',
                border: '1px solid hsl(var(--border) / 0.5)',
              }}
            >
              {/* Glow effect */}
              <div 
                className="absolute inset-0 opacity-50"
                style={{
                  background: 'radial-gradient(circle at 50% 0%, hsl(var(--primary) / 0.1) 0%, transparent 50%)',
                }}
              />
              
              <div className="relative z-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                  Ready to Transform Your Organization?
                </h2>
                <p className="text-muted-foreground mb-8">
                  Discover how three decades of technical innovation and strategic leadership can accelerate your goals.
                </p>
              <Link to="/work-with-me" className="btn-primary inline-flex items-center gap-2 group">
                  <span>Start the Conversation</span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </Layout>
  );
};

export default Index;